import { ClaimWorkflowConfigurationOption } from '../../api';
import { isUnsetConfigValue, WorkflowConfigValue } from '../../services/worker-configuration/worker-configuration.types';

export { isUnsetConfigValue };

/** Describes which editor control to render for a configuration option. */
export type OptionEditor =
    | { kind: 'boolean' }
    | { kind: 'enum'; choices: string[] }
    | { kind: 'number'; placeholder?: string }
    | { kind: 'text'; placeholder?: string };

type OptionDefinition = Pick<ClaimWorkflowConfigurationOption, 'valueType' | 'options'>;

/**
 * Converts camelCase or SCREAMING_SNAKE_CASE keys into sentence-case labels.
 * e.g. "retransmitClaimIfNonAcknowledged" → "Retransmit claim if non acknowledged"
 * e.g. "NEED_PRIMARY_TRANSMIT" → "Need primary transmit"
 */
export function humanizeKey(key: string): string {
    if (!key) {
        return '';
    }

    const words = key.includes('_')
        ? key.toLowerCase().split('_').filter(Boolean)
        : key
              .replace(/([a-z])([A-Z])/g, '$1 $2')
              .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
              .toLowerCase()
              .split(' ')
              .filter(Boolean);

    if (words.length === 0) {
        return '';
    }

    return [words[0].charAt(0).toUpperCase() + words[0].slice(1), ...words.slice(1)].join(' ');
}

/** Parses a comma-separated options string into individual values. */
export function parseOptions(options?: string): string[] {
    if (!options) {
        return [];
    }

    return options
        .split(',')
        .map((option) => option.trim())
        .filter(Boolean);
}

/** True when the options string defines a fixed enum list (two or more comma-separated values). */
export function isEnumOptionList(options?: string): boolean {
    return parseOptions(options).length >= 2;
}

/** Returns the single hint value when options defines exactly one entry (placeholder/default). */
export function getSingleOptionHint(options?: string): string | undefined {
    const parsed = parseOptions(options);
    return parsed.length === 1 ? parsed[0] : undefined;
}

/**
 * Maps API option metadata to the editor control used in the detail panel.
 * Multiple comma-separated options → enum dropdown; a single value → placeholder for free input.
 */
export function parseOptionEditor(option: OptionDefinition): OptionEditor {
    const valueType = option.valueType ?? '';
    const choices = parseOptions(option.options);

    if (valueType === 'boolean') {
        return { kind: 'boolean' };
    }

    if (choices.length >= 2) {
        return { kind: 'enum', choices };
    }

    if (valueType === 'integer' || valueType === 'number') {
        return { kind: 'number', placeholder: choices[0] };
    }

    return { kind: 'text', placeholder: choices[0] };
}

/** Maps stored boolean config to tri-state UI: true, false, or not set. */
export function parseBooleanTriState(value: WorkflowConfigValue | null | undefined): boolean | null {
    if (value === true) {
        return true;
    }

    if (value === false) {
        return false;
    }

    return null;
}

/** Converts an enum dropdown choice into the value stored in workflow config. */
export function coerceEnumChoice(choice: string, valueType?: string): WorkflowConfigValue {
    if (choice === '3+') {
        return '3+';
    }

    if (valueType === 'integer') {
        const parsed = Number.parseInt(choice, 10);
        return Number.isNaN(parsed) ? choice : parsed;
    }

    if (valueType === 'number') {
        const parsed = Number(choice);
        return Number.isNaN(parsed) ? choice : parsed;
    }

    return choice;
}

/**
 * Coerces a UI control value into the workflow config value shape.
 * Pass null or an empty string to clear (unset) the option.
 */
export function coerceConfigValue(
    raw: string | boolean | null,
    editor: OptionEditor,
    valueType?: string,
): WorkflowConfigValue | null {
    if (raw === null || raw === '') {
        return null;
    }

    if (editor.kind === 'boolean') {
        if (raw === true || raw === 'true') {
            return true;
        }

        if (raw === false || raw === 'false') {
            return false;
        }

        return null;
    }

    if (editor.kind === 'enum') {
        return coerceEnumChoice(String(raw), valueType);
    }

    if (editor.kind === 'number') {
        const text = String(raw);
        const parsed = Number(text);
        return Number.isNaN(parsed) ? text : parsed;
    }

    return String(raw);
}

/**
 * Formats a stored config value for binding to an editor control (select value or input text).
 */
export function formatEditorValue(
    value: WorkflowConfigValue | null | undefined,
    editor: OptionEditor,
    valueType?: string,
): string {
    if (isUnsetConfigValue(value)) {
        return '';
    }

    if (editor.kind === 'enum') {
        const hasThreePlus = editor.choices.includes('3+');

        if (hasThreePlus) {
            if (value === '3+') {
                return '3+';
            }

            const numericValue = Number(value);
            if (!Number.isNaN(numericValue) && numericValue >= 3) {
                return '3+';
            }
        }

        return String(value);
    }

    if (typeof value === 'boolean') {
        return String(value);
    }

    return String(value);
}

/** Select options for enum editors, with display labels (e.g. "3+" → "3 or more"). */
export function getEnumSelectOptions(
    editor: Extract<OptionEditor, { kind: 'enum' }>,
): Array<{ value: string; label: string }> {
    return editor.choices.map((choice) => ({
        value: choice,
        label: formatAllowedOption(choice),
    }));
}

/** Formats a single allowed option for display. */
export function formatAllowedOption(option: string): string {
    if (option === '3+') {
        return '3 or more';
    }

    return option;
}

/** Formats allowed options as a comma-separated display string. */
export function formatAllowedOptions(options?: string): string {
    return parseOptions(options).map(formatAllowedOption).join(', ');
}

/**
 * Formats a configured value for display.
 * Values of 3 or more map to "3 or more" when the allowed options include "3+".
 */
export function formatValue(
    value: WorkflowConfigValue | undefined,
    valueType?: string,
    allowedOptions?: string[],
): string {
    const parsedAllowedOptions = allowedOptions ?? [];
    const hasThreePlus = parsedAllowedOptions.includes('3+');

    if ((valueType === 'number' || valueType === 'integer') && hasThreePlus) {
        const numericValue = Number(value);
        if (!Number.isNaN(numericValue) && numericValue >= 3) {
            return '3 or more';
        }
    }

    if (typeof value === 'boolean') {
        return String(value);
    }

    return String(value);
}

/** Returns true when a workflow status matches the sidebar search query. */
export function matchesStatusSearch(workflowStatus: string, query: string): boolean {
    return matchesWorkerSearch(query, humanizeKey(workflowStatus), workflowStatus);
}

/** Returns true when any provided label matches the sidebar search query. */
export function matchesWorkerSearch(query: string, ...labels: string[]): boolean {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) {
        return true;
    }

    return labels.some((label) => label.toLowerCase().includes(normalizedQuery));
}

/** Splits a catalog message into a title and description at the first " - " or ",". */
export function splitCatalogOptionMessage(message: string): { description: string; title: string } {
    const trimmed = message.trim();
    if (!trimmed) {
        return { title: '', description: '' };
    }

    const dashIndex = trimmed.indexOf(' - ');
    const commaIndex = trimmed.indexOf(',');

    let splitIndex = -1;
    let delimiterLength = 0;

    if (dashIndex >= 0 && commaIndex >= 0) {
        if (dashIndex < commaIndex) {
            splitIndex = dashIndex;
            delimiterLength = 3;
        } else {
            splitIndex = commaIndex;
            delimiterLength = 1;
        }
    } else if (dashIndex >= 0) {
        splitIndex = dashIndex;
        delimiterLength = 3;
    } else if (commaIndex >= 0) {
        splitIndex = commaIndex;
        delimiterLength = 1;
    }

    if (splitIndex < 0) {
        return { title: trimmed, description: '' };
    }

    const title = trimmed.slice(0, splitIndex).trim();
    const description = trimmed.slice(splitIndex + delimiterLength).trim();

    return {
        title: title || trimmed,
        description,
    };
}

/** Header copy for catalog condition pickers backed by report counts. */
export function formatCatalogPickerResultCount(count: number, query?: string): string {
    const matching = query?.trim() ? ` matching “${query.trim()}”` : '';
    const valueLabel = count === 1 ? 'value' : 'values';

    return `${count.toLocaleString()} ${valueLabel} found${matching} in the last month`;
}

/** Count badge copy for catalog picker options. */
export function formatCatalogReportCountLabel(count: number): string {
    return `${count.toLocaleString()} found`;
}

/** True when catalog options for this condition kind are sorted by report frequency. */
export function isCatalogFrequencySortedKind(kind: string): boolean {
    return kind === 'carcCode' || kind === 'rarcCode' || kind === 'carcGroup' || kind === 'contains';
}

/** Accessible label for a boolean configuration value. */
export function getBooleanValueLabel(value: WorkflowConfigValue): string {
    return value === true ? 'Enabled' : 'Disabled';
}

/** Section header label for grouped sidebar navigation. */
export function getConfigurationSectionTitle(configured: boolean, count: number): string {
    return configured ? `Configured (${count})` : `Not configured (${count})`;
}
